import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import axiosClient from '../axios-client'; // Adjust path if needed

// Define the User type based on your data structure
interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  // Add other relevant user fields...
  isEmailVerified: boolean;
  userOnboarding: boolean;
  // ... etc
}

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  fetchUser: () => Promise<void>;
  clearUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const fetchUser = async () => {
    setIsLoading(true);
    try {
      // If there is no token stored, skip the request entirely
      const token = localStorage.getItem('ACCESS_TOKEN') || localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      // Assume you have a backend endpoint like '/api/users/me' or '/auth/me'
      // that returns the user data based on the HttpOnly cookie
      const response = await axiosClient.get<User>('/users/me'); // Adjust endpoint
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      setIsAuthenticated(false);
      // Handle specific errors, e.g., 401 might mean not logged in
    } finally {
      setIsLoading(false);
    }
  };

  const clearUser = () => {
    setUser(null);
    setIsAuthenticated(false);
    // Optionally call a backend logout endpoint
    // axiosClient.post('/auth/logout');
  };

  useEffect(() => {
    // Fetch user data when the provider mounts
    // This assumes the cookie might already be present
    fetchUser();
  }, []);

  return (
    <UserContext.Provider value={{ user, isLoading, isAuthenticated, fetchUser, clearUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};